import { z } from 'zod';
import { portalTheme } from '../../command-admin/schema/portalTheme.js';
import { RoleCode } from '../../enums/enum.js';
// oxlint-disable-next-line @typescript-eslint/no-namespace
export var Mcp;
(function (Mcp) {
    // role needed on the store a call names, mirroring the Corso app (enforced in the tool handlers)
    Mcp.Tool = {
        // ── Any role (Admin, Corso Admin, Staff) ──
        // every store the user holds a role on, with that role
        listStores: 'list_stores',
        // records: orders, returns, warranties, shipping claims and registrations
        listOrders: 'list_orders',
        getOrder: 'get_order',
        getOrderFulfillments: 'get_order_fulfillments',
        // what each line item can still be returned or warrantied for, and until when, as the customer portal computes it
        getOrderEligibility: 'get_order_eligibility',
        listClaims: 'list_claims',
        getClaim: 'get_claim',
        // the ledger's calculation, the customer's card payment and what was issued: how money moved on one claim
        getClaimMoney: 'get_claim_money',
        // the claim risk card: the last stored fraud risk assessment and its reasons
        getClaimRisk: 'get_claim_risk',
        // an exchange of an exchange traced back to the order the customer paid for, as the claim lineage modal shows it
        getClaimLineage: 'get_claim_lineage',
        listShippingClaims: 'list_shipping_claims',
        getShippingClaim: 'get_shipping_claim',
        listRegistrations: 'list_registrations',
        getRegistration: 'get_registration',
        // automations, read only: Staff answer "why did this happen to this order" from them
        listAutomations: 'list_automations',
        getAutomation: 'get_automation',
        // the facts automations read for an order, and a dry run of one trigger's automations against them
        getOrderFacts: 'get_order_facts',
        evaluateAutomations: 'evaluate_automations',
        // Corso Help, the same for every store, so a role on any one store is enough
        // built-in name: ChatGPT's knowledge-source contract (deep research, company knowledge) calls `search` and `fetch`
        search: 'search',
        // built-in name: returns the full article for a `search` hit's id
        fetch: 'fetch',
        listHelpArticles: 'list_help_articles',
        // ── Admin or Corso Admin (the Corso app's AdminProtector) ──
        // analytics: read-only ClickHouse SQL, scoped to the store by the mcp_reader row policy
        describeData: 'describe_data',
        query: 'query',
        // store setup, read only
        getStoreSettings: 'get_store_settings',
        listReturnLocations: 'list_return_locations',
        listClaimReasons: 'list_claim_reasons',
        listProductGroups: 'list_product_groups',
        listCustomFields: 'list_custom_fields',
        listShippingPlusRates: 'list_shipping_plus_rates',
        listIntegrations: 'list_integrations',
        listStoreUsers: 'list_store_users',
        // ── Corso staff only: registered just for Corso org members ──
        // presets and the store's current look, to ask the user before generating
        describePortalStyle: 'describe_portal_style',
        // a website's palette, font and corner radius, to match the portal to it
        importBrandFromSite: 'import_brand_from_site',
        // customer portal Custom CSS from a theme, shown for the user to approve; save_portal_css writes it
        generatePortalCss: 'generate_portal_css',
        // writes the generated CSS and the matching saved colors, only after the user says yes
        savePortalCss: 'save_portal_css',
        // puts back the Custom CSS and colors from before a save, only after the user says yes
        revertPortalCss: 'revert_portal_css',
        // a store's portal check-up: CSS outside the theming contract, readability, and drift from the saved colors
        auditPortalCss: 'audit_portal_css',
    };
    // the roles a person can hold on a store; Commerce_API_Admin is a credential role, never a person's
    const roleGroup = {
        // Corso Help is public, so reading it needs no store
        anyone: [],
        anyStoreRole: [RoleCode.admin, RoleCode.corsoAdmin, RoleCode.staff],
        // the merchant app's AdminProtector
        storeAdmin: [RoleCode.admin, RoleCode.corsoAdmin],
    };
    // every Tool needs an entry: the roles that may call it on a store
    Mcp.toolRoles = {
        [Mcp.Tool.listStores]: roleGroup.anyStoreRole,
        [Mcp.Tool.listOrders]: roleGroup.anyStoreRole,
        [Mcp.Tool.getOrder]: roleGroup.anyStoreRole,
        [Mcp.Tool.getOrderFulfillments]: roleGroup.anyStoreRole,
        [Mcp.Tool.getOrderEligibility]: roleGroup.anyStoreRole,
        [Mcp.Tool.listClaims]: roleGroup.anyStoreRole,
        [Mcp.Tool.getClaim]: roleGroup.anyStoreRole,
        [Mcp.Tool.getClaimMoney]: roleGroup.anyStoreRole,
        [Mcp.Tool.getClaimRisk]: roleGroup.anyStoreRole,
        [Mcp.Tool.getClaimLineage]: roleGroup.anyStoreRole,
        [Mcp.Tool.listShippingClaims]: roleGroup.anyStoreRole,
        [Mcp.Tool.getShippingClaim]: roleGroup.anyStoreRole,
        [Mcp.Tool.listRegistrations]: roleGroup.anyStoreRole,
        [Mcp.Tool.getRegistration]: roleGroup.anyStoreRole,
        [Mcp.Tool.listAutomations]: roleGroup.anyStoreRole,
        [Mcp.Tool.getAutomation]: roleGroup.anyStoreRole,
        [Mcp.Tool.getOrderFacts]: roleGroup.anyStoreRole,
        [Mcp.Tool.evaluateAutomations]: roleGroup.anyStoreRole,
        [Mcp.Tool.search]: roleGroup.anyone,
        [Mcp.Tool.fetch]: roleGroup.anyone,
        [Mcp.Tool.listHelpArticles]: roleGroup.anyone,
        [Mcp.Tool.describeData]: roleGroup.storeAdmin,
        [Mcp.Tool.query]: roleGroup.storeAdmin,
        [Mcp.Tool.getStoreSettings]: roleGroup.storeAdmin,
        [Mcp.Tool.listReturnLocations]: roleGroup.storeAdmin,
        [Mcp.Tool.listClaimReasons]: roleGroup.storeAdmin,
        [Mcp.Tool.listProductGroups]: roleGroup.storeAdmin,
        [Mcp.Tool.listCustomFields]: roleGroup.storeAdmin,
        [Mcp.Tool.listShippingPlusRates]: roleGroup.storeAdmin,
        [Mcp.Tool.listIntegrations]: roleGroup.storeAdmin,
        [Mcp.Tool.listStoreUsers]: roleGroup.storeAdmin,
        [Mcp.Tool.describePortalStyle]: roleGroup.storeAdmin,
        [Mcp.Tool.importBrandFromSite]: roleGroup.storeAdmin,
        [Mcp.Tool.generatePortalCss]: roleGroup.storeAdmin,
        [Mcp.Tool.savePortalCss]: roleGroup.storeAdmin,
        [Mcp.Tool.revertPortalCss]: roleGroup.storeAdmin,
        [Mcp.Tool.auditPortalCss]: roleGroup.storeAdmin,
    };
    // what any tool call returns (the MCP spec's CallToolResult): text for the model, structuredContent for apps, isError when the tool refused
    Mcp.callToolResultSchema = z.object({
        content: z.array(z.looseObject({ type: z.string(), text: z.string().optional() })),
        structuredContent: z.unknown().optional(),
        isError: z.boolean().optional(),
    });
    // `query`'s result, which the server returns and the in-app chat charts
    Mcp.queryResultSchema = z.object({
        columns: z.array(z.object({ name: z.string(), type: z.string() })),
        rows: z.array(z.array(z.unknown())),
        rowCount: z.number(),
        truncated: z.boolean(),
        elapsedSeconds: z.number().optional(),
        rowsRead: z.number().optional(),
    });
    // `search`'s result, which the in-app chat shows as sources
    Mcp.helpSearchResultSchema = z.object({
        results: z.array(z.object({
            id: z.string().describe('Pass to fetch for the full article.'),
            title: z.string(),
            url: z.string(),
            text: z.string().describe('A relevant snippet.'),
        })),
    });
    Mcp.CssFindingSeverity = {
        // breaks, hides or disables part of the portal, or makes it unreadable
        harmful: 'harmful',
        // works today but sits outside the contract, so a portal change can break it silently
        brittle: 'brittle',
    };
    Mcp.cssFindingSchema = z.object({
        severity: z.enum(Mcp.CssFindingSeverity),
        line: z.number(),
        selector: z.string().optional(),
        declaration: z.string().optional(),
        issue: z.string().describe('What is wrong and what it does to the portal.'),
    });
    // how readable one text and background pair is
    const contrastCheckSchema = z.object({
        pair: z.string(),
        text: z.string().describe('The text color, hex.'),
        background: z
            .string()
            .describe('What it sits on, hex (glass already blended over the page).'),
        suggestedText: z
            .string()
            .optional()
            .describe('When it fails: a plain white or dark text color that would pass. A suggestion only; never apply it unasked.'),
        ratio: z.number().describe('Over the theme’s page color.'),
        minimum: z.number(),
        passes: z.boolean(),
        overDarkPage: z
            .number()
            .optional()
            .describe('Glass surfaces only: the ratio over a black page, the worst a photo or an embedded merchant page could do.'),
        dependsOnSite: z
            .boolean()
            .optional()
            .describe('Embedded only: in the merchant’s site this text sits on their page, so ratio holds only on the portal’s own URL.'),
    });
    // `generate_portal_css`'s result, which the in-app chat shows as a card
    Mcp.portalCssResultSchema = z.object({
        css: z
            .string()
            .describe('The complete Custom CSS, ready to paste over what is saved: the generated block plus keptCss.'),
        startedFrom: z
            .enum(['existing_css', 'imported_css', 'preset', 'saved_colors'])
            .describe('existing_css: the theme read back from CSS this tool generated before. imported_css: what was read from CSS written by hand or in another tool (see imported), on Default with the saved colors. preset: the preset asked for, with its own colors. saved_colors: the Default preset with the store’s saved brand and background colors.'),
        theme: portalTheme.describe('Every theme field the CSS was built from.'),
        imported: z
            .array(z.string())
            .describe('What was read from hand-written CSS into the theme, and what couldn’t be.'),
        needsDecision: z
            .boolean()
            .describe('true: css is empty until the user decides what happens to handWrittenCss (existingCss) or to the conflicts (conflicts).'),
        handWrittenCss: z
            .string()
            .describe('Saved CSS this tool didn’t write: all of it, or what was added around or edited inside the block it wrote.'),
        removedByHand: z
            .array(z.object({ selector: z.string(), property: z.string() }))
            .describe('Declarations deleted by hand from the block this tool wrote; the new look puts them back unless the user keeps their CSS.'),
        conflicts: z
            .array(z.object({
            selector: z.string(),
            property: z.string(),
            existing: z.string(),
            generated: z.string(),
        }))
            .describe('Where handWrittenCss and the new theme set the same thing to different values.'),
        keptCss: z
            .string()
            .describe('The part of handWrittenCss carried into css, after the existingCss and conflicts choices.'),
        keptCssFindings: z
            .array(Mcp.cssFindingSchema)
            .describe('audit_portal_css on keptCss.'),
        replacedCss: z
            .string()
            .describe('handWrittenCss left out of css because existingCss was replace.'),
        contrast: z.array(contrastCheckSchema),
        unsupported: z
            .array(z.object({ request: z.string(), reason: z.string() }))
            .describe('Asks Custom CSS can’t express, logged for Corso engineering.'),
        portalUrl: z
            .string()
            .nullable()
            .describe('The store’s customer portal, to look at the result.'),
        links: z.object({ appUrl: z.string(), helpUrl: z.string() }),
    });
    // `describe_portal_style`'s result
    Mcp.portalStyleSummarySchema = z.object({
        presets: z.array(z.object({ name: z.string(), description: z.string() })),
        current: z.object({
            primaryColor: z.string().nullable(),
            backgroundColor: z.string().nullable(),
            customCss: z
                .enum(['none', 'from_this_tool', 'edited_by_hand', 'hand_written'])
                .describe('Where the saved Custom CSS came from: none, generated by this tool, generated then edited by hand, or written by hand.'),
            theme: portalTheme
                .optional()
                .describe('The theme this tool last generated, when it did.'),
        }),
        contrast: z
            .array(contrastCheckSchema)
            .describe('How readable the portal is today, from the saved CSS and colors.'),
        portalUrl: z
            .string()
            .nullable()
            .describe('The store’s customer portal, to look at the result.'),
        links: z.object({ appUrl: z.string(), helpUrl: z.string() }),
    });
    // `import_brand_from_site`'s result
    const brandColor = (role) => z.string().nullable().describe(`The site’s ${role} color as hex, or null.`);
    Mcp.brandImportSchema = z.object({
        url: z.string(),
        colorScheme: z
            .enum(['light', 'dark'])
            .nullable()
            .describe('Whether the site is light or dark overall.'),
        colors: z.object({
            primary: brandColor('primary brand (main buttons)'),
            secondary: brandColor('secondary'),
            accent: brandColor('accent'),
            background: brandColor('page background'),
            textPrimary: brandColor('main text'),
            textSecondary: brandColor('muted text'),
            link: brandColor('link'),
            error: brandColor('error'),
        }),
        fonts: z.object({
            body: z.string().nullable().describe('Its body font family name.'),
            heading: z.string().nullable().describe('Its heading font family name.'),
        }),
        borderRadius: z.object({
            button: z.number().nullable().describe('Its button corner radius in px.'),
            default: z
                .number()
                .nullable()
                .describe('Its default card and panel corner radius in px.'),
        }),
        logoUrl: z.string().nullable(),
    });
    // `save_portal_css`'s result
    Mcp.portalCssSaveSchema = z.object({
        css: z.string(),
        primaryColor: z.string(),
        backgroundColor: z.string(),
        emailHeaderColorChanged: z
            .boolean()
            .describe('The saved primary color changed, which also recolors the header of every customer email.'),
        portalUrl: z
            .string()
            .nullable()
            .describe('The store’s customer portal, to look at the result.'),
        links: z.object({ appUrl: z.string(), helpUrl: z.string() }),
    });
    // `revert_portal_css`'s result
    Mcp.portalCssRevertSchema = z.object({
        restoredFrom: z
            .string()
            .describe('When the look that was replaced had been saved over, ISO.'),
        remaining: z
            .number()
            .describe('How many earlier looks can still be put back.'),
        primaryColor: z.string(),
        backgroundColor: z.string().optional(),
        emailHeaderColorChanged: z
            .boolean()
            .describe('The saved primary color changed back, which also recolors the header of every customer email.'),
        portalUrl: z
            .string()
            .nullable()
            .describe('The store’s customer portal, to look at the result.'),
        links: z.object({ appUrl: z.string(), helpUrl: z.string() }),
    });
    // `audit_portal_css`'s result
    Mcp.portalCssAuditSchema = z.object({
        source: z
            .enum(['saved', 'provided'])
            .describe('saved: the store’s Custom CSS. provided: the css passed in.'),
        findings: z.array(Mcp.cssFindingSchema),
        storeFindings: z
            .array(z.object({
            severity: z.enum(Mcp.CssFindingSeverity),
            issue: z.string(),
        }))
            .describe('Problems with the look as a whole rather than one line of CSS.'),
        contrast: z
            .array(contrastCheckSchema)
            .describe('Saved only: how readable the portal is today.'),
        portalUrl: z
            .string()
            .nullable()
            .describe('The store’s customer portal, to look at the result.'),
        links: z.object({ appUrl: z.string(), helpUrl: z.string() }),
    });
})(Mcp || (Mcp = {}));
//# sourceMappingURL=mcp.js.map